/**
 * Market data the account panels need: outcomeMeta (side names / titles) and mids (mark outcome shares).
 * Kept local to the account panels; if a shared hl/mids.ts lands, swap useMids for it.
 * Mids: REST allMids snapshot, then WS allMids (every ~5 s, includes "#<enc>" outcome coins; STREAMS.md).
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useOutcomeMeta as useCatalog } from '../catalog/useOutcomeMeta';
import { hlQueryKey, useHlEnv, useInfoClient, useOnWsReconnect, useSubscription } from '../hl/react';
import type { AllMidsResponse, OutcomeMetaResponse } from '../hl/types';

export function useOutcomeMeta(enabled = true): OutcomeMetaResponse | undefined {
  return useCatalog(enabled).data;
}

export function useMids(enabled = true): AllMidsResponse | undefined {
  const env = useHlEnv();
  const info = useInfoClient();
  const qc = useQueryClient();
  const key = hlQueryKey(env, 'allMids');
  const q = useQuery<AllMidsResponse, Error>({
    queryKey: key,
    queryFn: ({ signal }) => info.allMids({ signal }),
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  useSubscription(enabled ? { type: 'allMids' } : null, (msg) => {
    if (msg?.mids) qc.setQueryData<AllMidsResponse>(key, (prev) => ({ ...prev, ...msg.mids }));
  });
  useOnWsReconnect('aggregate', () => {
    if (enabled) void qc.refetchQueries({ queryKey: key, exact: true });
  });
  return q.data;
}
